﻿import { logError } from '@core/logging';
import { BaseApiResponse, ChatSettings, SlideDeck, SlideDeckStatus, VindralChannelInformation, WebinarDataWithAssets } from '@core/swagger-client';
import {
    BroadcastQuality,
    Chapter,
    ChatChannel,
    ChatChannelIds,
    ChatMessage,
    ChatMessageRelationType,
    ClientType,
    DisconnectReason,
    Exam,
    IngestType,
    LiveStatus,
    ParticipantAnswers,
    ParticipantSession,
    PlayerStatsSample,
    PollOptionResult,
    PollVoteSlim,
    PollWithResults,
    RecorderStatus,
    SpeakerStatus,
    SpeakerStatusInfo,
    StreamValidation,
    Subtitle,
    SubtitlesTrack,
    Survey,
    VideoLayout,
    WebinarStatus
} from '@core/types';
import { CaseReducer, PayloadAction, createSlice } from '@reduxjs/toolkit';
import type { IWebrtcIngestStats } from '@vindral/webrtc-ingest-sdk';
import { Duration } from 'luxon';
import type { MoqFallbackReason, MoqPublisherStatsSample } from '../moq';
import { ChatChannelUnreadMessageCount } from '../signal-r/live-webinar-connection';

export enum PresenterStatus {
    Connecting,
    TooEarly,
    NoBooking,
    SelectDevices,
    WaitingForTimeslot,
    Initializing,
    Ready,
    Countdown,
    Live,
    Ended,
    Finished,
    Error,
    ErrorUnsupportedBrowser
}

export interface ChatChannelActivity extends ChatChannel {
    unreadTimestamp: string;
    channelName?: string;
}

export interface JoinedChatChannelInfo {
    channelName?: string;
    noOfUnreadMessages: number;
    unreadTimestamp: string;
}

export interface JoinedChatChannel extends ChatChannel {
    continued: boolean;
}

export interface JoinWebinarRequest {
    webinarId: string;
    linkCode?: string;
    authenticationCode?: string;
    anonymousId?: string;
    anonymousAuthorizationCode?: string;
    clientType: ClientType;
}

export interface ConnectionStatus {
    status: LiveStatus;
    disconnectReason?: DisconnectReason | undefined;
    error?: BaseApiResponse | undefined;
}

export interface IngestInformation {
    url: string;
    baseUrl: string;
    streamKey: string;
}

export enum BookingStatus {
    Unavailable,
    Confirmed,
    Ended,
    Cancelled,
    Deleted
}

export interface Booking {
    companyCode: string;
    webinarId: string;
    tierId: string;
    status: BookingStatus;
    title: string;
    earlyStartOn: string;
    startOn: string;
    warningOn: string;
    endOn: string;
}

export enum ActivePollStatus {
    Inactive,
    Active,
    Closed
}

export enum WebinarExtendedResult {
    Success,
    Failed,
    NotAvailable
}

export interface ChatRejection {
    channelId: string;
    message: string;
    relatedMessageId?: string;
    relationType?: ChatMessageRelationType;
    errorCode?: string;
}

export interface LiveWebinarState {
    isInitialized: boolean;
    browserSupported: boolean;

    webinarId: string | null;
    webinarData: WebinarDataWithAssets;

    tierId?: string | null;
    earlyStartOn: string | null;
    startOn: string | null;
    warningOn: string | null;
    endOn: string | null;

    startedOn: string | null;
    endedOn: string | null;

    serverTime: string | null;

    _videoLayout: VideoLayout;
    videoLayout: VideoLayout;

    activeSlideNumber: number | null;
    slideDeck: SlideDeck;
    activeChapterId: string | null;
    // Non-optional on purpose: while this was optional, an initialState without it typechecked, and
    // leaveWebinar's Object.assign(state, initialState) then could not clear it — leaking the
    // previous webinar's chapter into the next one.
    activeChapter: Chapter | null;
    chapters: Chapter[];

    polls: PollWithResults[];
    activePoll: PollWithResults | null;
    activePollId: string | null;
    activePollStatus: ActivePollStatus;
    myPollVotes: PollVoteSlim[];

    onlineParticipants: number;
    participants: ParticipantSession[];
    liveStatus: LiveStatus;
    streamValidation: StreamValidation;
    hadExternalIngest: boolean;
    dataConnection: ConnectionStatus;
    videoConnection: ConnectionStatus;
    me: ParticipantSession | null;
    presenter: ParticipantSession | null;
    vindralChannel: VindralChannelInformation | null;
    srtIngestInformation?: IngestInformation | null;
    rtmpIngestInformation?: IngestInformation | null;

    // Call-in speaker roster (presenter/moderator/speaker clients only, via the SpeakersChannel group)
    speakers: SpeakerStatusInfo[];
    mySpeakerStatus: SpeakerStatus;
    notifiedAt: string | null;

    subtitles: SubtitlesTrack[];
    activeSubtitleTrackId?: string | null;
    activeSubtitles?: Subtitle[];
    timestamp: string | null;

    unreadChatMessages: number;
    chatChannels: { [channelId: string]: ChatChannelActivity };
    joinedChannels: string[];
    joinedChannelsInfo: { [channelId: string]: JoinedChatChannelInfo };
    chatSettings: ChatSettings;

    survey: Survey | null;
    surveyAnswers: ParticipantAnswers | null;

    exam: Exam | null;
    examAnswers: ParticipantAnswers | null;

    recorderStatus: RecorderStatus;
    recorderStatusUpdatedOn: string | null;

    // Optional, used in broadcast
    isPresenter: boolean | null;
    isActivePresenter: boolean | null;
    isTooEarly: boolean | null;
    showTooEarly: boolean | null;
    showReconnectWarning: boolean | null;
    webinarExtendedResult?: WebinarExtendedResult | null;

    showEndWebinarWarning: boolean | null;
    remainingTimeToEnd: number | null;
    hasEnded: boolean | null;

    presenterStatus: PresenterStatus | null;
    duration: number | null;
    remainingTimeToStart: number | null;
    broadcastState: 'broadcasting' | 'initializing' | 'not_broadcasting' | 'error' | null;

    /** The broadcast state before the latest update; only a fall from 'broadcasting' is a loss. */
    previousBroadcastState: 'broadcasting' | 'initializing' | 'not_broadcasting' | 'error' | null;

    /**
     * Bumped on every new interruption. A reconciliation that started before the latest interruption
     * carries the old number and must not clear it.
     */
    mediaInterruptionSeq: number;

    /**
     * Whether our video connection has dropped at any point since the server last accepted a resume.
     * Sticky on purpose: a drop that already recovered still counts, because the audience lost the
     * stream when it happened. Reported to the server on reconnect and cleared only once it has been
     * accounted for.
     */
    mediaInterrupted: boolean;

    /**
     * Whether we must stop putting the real programme on the wire, decided locally and without waiting
     * for the server. Everything that publishes is gated on this as well as on liveStatus, because
     * liveStatus only ever arrives over the connection that just failed.
     */
    locallyPaused: boolean;

    audioDeviceId: string | null;
    videoDeviceId: string | null;
    moqPublisher: boolean | null;
    /** Set when the MoQ publisher gave way to WebRTC on this page; cleared when the presenter turns MoQ on again. */
    moqFallback: { reason: MoqFallbackReason; code: string | null; at: number } | null;
    broadcastQuality: BroadcastQuality | null;
    audioScore: number | null;
    videoScore: number | null;

    lastServerAction?: {
        method: string;
        result: BaseApiResponse | null;
    } | null;

    failedServerActions?:
        | {
              method: string;
              result: BaseApiResponse | null;
          }[]
        | null;

    /** The last send/reply/forward the server refused; the composer for that channel consumes it once. */
    lastChatRejection?: ChatRejection | null;
}

const initialState: LiveWebinarState = {
    isInitialized: false,
    browserSupported: !(
        !globalThis.window ||
        !('RTCPeerConnection' in globalThis.window || 'mozRTCPeerConnection' in globalThis.window || 'webkitRTCPeerConnection' in globalThis.window) ||
        !('AudioContext' in globalThis.window)
    ),

    webinarId: null,
    webinarData: <WebinarDataWithAssets>(<Partial<WebinarDataWithAssets>>{}), // Partial to avoid undefined errors

    earlyStartOn: null,
    startOn: null,
    warningOn: null,
    endOn: null,

    startedOn: null,
    endedOn: null,

    serverTime: null,

    subtitles: [],
    activeSubtitleTrackId: null,
    activeSubtitles: [],
    timestamp: null,

    // DO NOT USE _videoLayout directly, use videoLayout instead
    _videoLayout: VideoLayout.Default,
    videoLayout: VideoLayout.Default,

    activeSlideNumber: null,
    slideDeck: {
        slides: [],
        totalSlides: 0,
        filename: null,
        status: SlideDeckStatus.None
    },
    activeChapterId: null,
    activeChapter: null,
    chapters: [],

    polls: [],
    activePoll: null,
    activePollId: null,
    activePollStatus: ActivePollStatus.Inactive,
    myPollVotes: [],

    onlineParticipants: 0,
    participants: [],

    liveStatus: LiveStatus.Offline,
    streamValidation: StreamValidation.Unknown,
    hadExternalIngest: false,
    dataConnection: {
        status: LiveStatus.Offline
    },
    videoConnection: {
        status: LiveStatus.Offline
    },

    me: null,
    presenter: null,
    vindralChannel: null,

    speakers: [],
    mySpeakerStatus: SpeakerStatus.SettingUp,
    notifiedAt: null,

    unreadChatMessages: 0,
    chatChannels: {},
    joinedChannels: [],
    joinedChannelsInfo: {},
    chatSettings: { enableOnDemandChatForwarding: false },

    survey: null,
    surveyAnswers: null,

    exam: null,
    examAnswers: null,

    recorderStatus: RecorderStatus.Idle,
    recorderStatusUpdatedOn: null,

    isPresenter: null,
    isActivePresenter: null,
    isTooEarly: null,
    showTooEarly: null,
    showReconnectWarning: null,
    showEndWebinarWarning: null,
    remainingTimeToEnd: null,
    hasEnded: null,
    presenterStatus: null,
    duration: null,
    remainingTimeToStart: null,
    broadcastState: null,
    previousBroadcastState: null,
    mediaInterruptionSeq: 0,
    mediaInterrupted: false,
    locallyPaused: false,
    audioDeviceId: null,
    videoDeviceId: null,
    moqPublisher: null,
    moqFallback: null,
    broadcastQuality: null,
    audioScore: null,
    videoScore: null,
    lastChatRejection: null
};

const resetState: CaseReducer<LiveWebinarState> = (state) => {
    Object.assign(state, initialState);
};

interface ActivePollUpdatedRequest {
    activePollId: string;
    activePollStatus: ActivePollStatus;
}

interface PollVotesUpdatedRequest {
    pollId: string;
    pollVotes: { [optionId: string]: PollOptionResult };
}

interface VoteOnPollRequest {
    pollId: string;
    optionId: string;
}

interface StoreChatMessageRequest {
    channelId: string;
    message: ChatMessage;
}

const storeChatMessage: CaseReducer<LiveWebinarState, PayloadAction<StoreChatMessageRequest>> = (state, action) => {
    if (!(action.payload.channelId in state.chatChannels)) {
        state.chatChannels[action.payload.channelId] = {
            channelId: action.payload.channelId,
            messages: [],
            noOfUnreadMessages: 0,
            unreadTimestamp: new Date().toISOString()
        };
    }
    const channel = state.chatChannels[action.payload.channelId];

    // If we already have the message, ignore it
    if (channel.messages.findIndex((c) => c.messageId === action.payload.message.messageId) !== -1) {
        return;
    }
    channel.messages = [...channel.messages, action.payload.message];
    if (ChatChannelIds.isSystemChannel(action.payload.channelId)) {
        channel.noOfUnreadMessages++;
    }

    // If this is the "All channel" then only keep an arbitrary limit of 20 messages
    if (channel.channelId === ChatChannelIds.allChannel) {
        while (channel.messages.length > 50) {
            channel.messages = [...channel.messages.slice(1)];
        }
    }

    if (!ChatChannelIds.isSystemChannel(action.payload.channelId)) {
        // Try to find the channel name
        const participant = state.participants.find((p) => p.participantId === action.payload.channelId);
        let channelName = participant?.nickname || participant?.participantId;
        if (!channelName) {
            const sender = channel.messages.find((m) => m.senderParticipantId === action.payload.channelId);
            if (sender) {
                channelName = sender.senderNickname;
            }
        }

        // If we have a channel name, update the channel name
        if (channelName && channel.channelName !== channelName) {
            channel.channelName = channelName;
        }
    }

    // If we joined this channel, update the joined channels
    const joinedChannel = state.joinedChannelsInfo[action.payload.channelId];
    if (!joinedChannel) return;

    joinedChannel.noOfUnreadMessages = channel.noOfUnreadMessages;
    joinedChannel.unreadTimestamp = channel.unreadTimestamp;
};

const storePresenterStatus: CaseReducer<LiveWebinarState> = (state) => {
    if (!state.isPresenter) {
        return;
    }

    state.isActivePresenter = state.me?.sessionId === state.presenter?.sessionId;
    if (!state.isActivePresenter) {
        state.showReconnectWarning = false;
    }
    if (!state.browserSupported) {
        state.presenterStatus = PresenterStatus.ErrorUnsupportedBrowser;
        return;
    }

    if (!state.isInitialized) {
        state.showTooEarly = true;
        state.showReconnectWarning = false;
        state.showEndWebinarWarning = false;
        state.presenterStatus = PresenterStatus.Connecting;
        return;
    }

    if (state.webinarData.status === WebinarStatus.Expired || state.webinarData.status === WebinarStatus.Cancelled) {
        state.presenterStatus = PresenterStatus.Finished;
        state.showReconnectWarning = false;
        state.showEndWebinarWarning = false;

        // Reset device IDs when webinar has expired
        state.videoDeviceId = null;
        state.audioDeviceId = null;
        return;
    }

    const serverTime = state.serverTime ? new Date(state.serverTime) : new Date();
    if (state.webinarData.status === WebinarStatus.Ended) {
        const endedOnOrEndOn = state.endedOn || state.endOn;
        const endedOnDate = endedOnOrEndOn ? new Date(endedOnOrEndOn) : new Date();

        const startedOnOrStartOn = state.startedOn || state.startOn;
        const startedOnDate = startedOnOrStartOn ? new Date(startedOnOrStartOn) : new Date();

        state.duration = endedOnDate.getTime() - startedOnDate.getTime();
        state.showReconnectWarning = false;
        state.hasEnded = true;

        const hourAfterEndOn = new Date(endedOnDate.getTime() + 60 * 60 * 1000);
        if (hourAfterEndOn < (serverTime ?? new Date())) {
            state.presenterStatus = PresenterStatus.Finished;
        } else {
            state.presenterStatus = PresenterStatus.Ended;
        }

        // Reset device IDs when webinar has ended
        state.videoDeviceId = null;
        state.audioDeviceId = null;
        return;
    }

    if (!state.startOn || !state.earlyStartOn || !state.warningOn || !state.endOn) {
        // If we don't have a booking, we cannot start the webinar
        state.presenterStatus = PresenterStatus.NoBooking;
        return;
    }

    const earlyStartOnTime = new Date(state.earlyStartOn);
    const startOnTime = new Date(state.startOn);
    const warningOnTime = new Date(state.warningOn);
    const endOnTime = new Date(state.endOn);
    state.showEndWebinarWarning = state.webinarData.status === WebinarStatus.Started && warningOnTime < serverTime;
    state.remainingTimeToEnd = Math.max(0, endOnTime.getTime() - serverTime.getTime());
    state.remainingTimeToStart = Math.max(-1, startOnTime.getTime() - serverTime.getTime());
    state.isTooEarly = serverTime < earlyStartOnTime;
    state.hasEnded = endOnTime < serverTime;

    if (state.startedOn) {
        const startedOnTime = new Date(state.startedOn);
        const endedOnTime = state.endedOn ? new Date(state.endedOn) : serverTime;
        state.duration = endedOnTime.getTime() - startedOnTime.getTime();
    }

    if (new Date(serverTime.getTime() + 5 * 60 * 1000) < earlyStartOnTime && state.showTooEarly) {
        state.presenterStatus = PresenterStatus.TooEarly;
        return;
    }

    if (!state.audioDeviceId || !state.videoDeviceId) {
        state.presenterStatus = PresenterStatus.SelectDevices;
        return;
    }

    if (!state.vindralChannel) {
        state.presenterStatus = PresenterStatus.WaitingForTimeslot;
        return;
    }

    if (state.broadcastState !== 'broadcasting') {
        state.presenterStatus = PresenterStatus.Initializing;
        return;
    }

    if (state.liveStatus === LiveStatus.Paused && state.presenterStatus != PresenterStatus.Ready && state.presenterStatus != PresenterStatus.Countdown) {
        state.presenterStatus = PresenterStatus.Ready;
        return;
    }

    // The local gate outranks the server's status here. While it is closed the server's last word may
    // be stale -- it was sent over the connection that failed -- and treating it as live is what would
    // put the camera and microphone back on the wire behind the presenter's back. Audio muting hangs
    // off this too, through the audioMuted the publishers are given.
    if (state.liveStatus === LiveStatus.Live && !state.locallyPaused) {
        state.presenterStatus = PresenterStatus.Live;
        return;
    }
};

const filteredSubtitles = (trackId: string | null, subtitles: SubtitlesTrack[], timestamp: string | null): Subtitle[] => {
    if (!trackId || !timestamp) {
        return [];
    }

    const track = subtitles.find((t) => t.trackId === trackId);
    if (!track) {
        return [];
    }

    const time = Duration.fromISOTime(timestamp);
    return track.subtitles.filter((s) => {
        const start = Duration.fromISOTime(s.start);
        const end = Duration.fromISOTime(s.end);
        return time >= start && time <= end;
    });
};

export const liveWebinarSlice = createSlice({
    name: 'liveWebinar',
    initialState,
    reducers: {
        initializedWebinar: (state) => {
            state.isInitialized = true;
            storePresenterStatus(state, null!);
        },
        joinWebinar: (state, action: PayloadAction<JoinWebinarRequest>) => {
            resetState(state, null!);
            state.webinarId = action.payload.webinarId;
            if (action.payload.clientType === ClientType.presenter) {
                state.isPresenter = true;
                storePresenterStatus(state, action);
            }
        },
        leaveWebinar: (state) => {
            resetState(state, null!);
        },
        webinarDataUpdated: (state, action: PayloadAction<WebinarDataWithAssets>) => {
            if (!state.webinarData) {
                state.webinarData = action.payload;
            }
            Object.assign(state.webinarData, action.payload);
            state.startedOn = action.payload.startedOn || null;
            state.endedOn = action.payload.endedOn || null;
            if (state.webinarData.status === WebinarStatus.Ended) {
                // This will cause the broadcast to actually stop
                state.vindralChannel = null;
            }
            storePresenterStatus(state, action);
        },
        activeChapterUpdated: (state, action: PayloadAction<string | null>) => {
            state.activeChapterId = action.payload;
            // find() yields undefined for an unknown or null id; normalise to null so the field is
            // always present and always resettable.
            state.activeChapter = state.chapters.find((c) => c.chapterId === action.payload) ?? null;
        },

        chaptersUpdated: (state, action: PayloadAction<Chapter[]>) => {
            state.chapters = action.payload;
        },
        activeSlideUpdated: (state, action: PayloadAction<number | null>) => {
            state.activeSlideNumber = action.payload;
        },

        pollsUpdated: (state, action: PayloadAction<PollWithResults[]>) => {
            // Keep votes and weighted votes in sync
            for (let poll of action.payload) {
                let existing = state.polls.find((p) => p.pollId === poll.pollId);
                if (!existing) {
                    continue;
                }

                for (let option of poll.options) {
                    let existingOption = existing.options.find((o) => o.optionId === option.optionId);
                    if (!existingOption) {
                        continue;
                    }

                    option.votes = existingOption.votes;
                    option.weightedVotes = existingOption.weightedVotes;
                }

                poll.totalVotes = poll.options.reduce((total, o) => total + o.votes, 0);
                poll.totalWeightedVotes = poll.options.reduce((total, o) => total + o.weightedVotes, 0);
            }

            state.polls = action.payload;
            if (state.activePollId) {
                state.activePoll = state.polls.find((p) => p.pollId === state.activePollId) ?? null;
            }
        },
        activePollUpdated(state, action: PayloadAction<ActivePollUpdatedRequest>) {
            state.activePollStatus = action.payload.activePollStatus;

            if (action.payload.activePollStatus === ActivePollStatus.Inactive) {
                state.activePoll = null;
                state.activePollId = null;
                return;
            }

            state.activePoll = state.polls.find((p) => p.pollId === action.payload.activePollId) ?? null;
            state.activePollId = action.payload.activePollId;
        },
        pollVotesUpdated(state, action: PayloadAction<PollVotesUpdatedRequest>) {
            const poll = state.polls.find((p) => p.pollId === action.payload.pollId);
            if (!poll) return;

            poll.options = poll.options.map((o) => {
                const votes = action.payload.pollVotes[o.optionId] ?? { votes: 0, weightedVotes: 0 };
                return {
                    ...o,
                    ...votes
                };
            });
            poll.totalVotes = poll.options.reduce((total, o) => total + o.votes, 0);
            poll.totalWeightedVotes = poll.options.reduce((total, o) => total + o.weightedVotes, 0);

            if (state.activePollId === action.payload.pollId) {
                state.activePoll = poll;
            }
        },
        slideDeckUpdated: (state, action: PayloadAction<SlideDeck>) => {
            state.slideDeck = action.payload;

            if (state.videoLayout === VideoLayout.DesktopSharing) return;
            if (state._videoLayout !== VideoLayout.Default) return;

            if (state.slideDeck.slides.length === 0) {
                state.videoLayout = VideoLayout.VideoOnly;
            } else {
                state.videoLayout = VideoLayout.Classic;
            }
        },
        liveStatusUpdated: (state, action: PayloadAction<LiveStatus>) => {
            state.liveStatus = action.payload;

            // The gate exists because the server's word can be stale while the connection is down. A
            // status that arrives over a live connection, with no interruption of ours still unreported,
            // is the server's current word -- there is nothing left for a reconciliation to add, so the
            // gate need not wait for one. This is also what lets a presenter recover by Pause/Resume if
            // a reconciliation call ever failed.
            if (state.dataConnection.status === LiveStatus.Live && !state.mediaInterrupted) {
                state.locallyPaused = false;
            }

            storePresenterStatus(state, action);
        },
        streamValidationUpdated: (state, action: PayloadAction<StreamValidation>) => {
            state.streamValidation = action.payload;
        },
        ingestTypeHydrated: (state, action: PayloadAction<IngestType>) => {
            // What the server already knows when this client joins. A presenter reopening the
            // device dialog gets a camera preselected from a real media track, so without this the
            // broadcaster would have no idea an encoder had ever been chosen.
            if (isExternalEncoder(action.payload)) {
                state.hadExternalIngest = true;
            }
        },
        joinedWebinar: (state, action: PayloadAction<ParticipantSession[]>) => {
            state.participants = [
                ...state.participants,
                ...action.payload.filter((p) => state.participants.findIndex((r) => r.sessionId === p.sessionId) === -1)
            ];
            // Count distinct number of viewers based on participantId
            const distinctParticipants = new Set<string>();
            for (const participant of state.participants) {
                distinctParticipants.add(participant.participantId);
            }
            state.onlineParticipants = distinctParticipants.size;
        },
        leftWebinar: (state, action: PayloadAction<ParticipantSession[]>) => {
            // TODO: Mark participants as "Offline" and remove after x seconds
            state.participants = state.participants.filter((p) => action.payload.findIndex((r) => r.sessionId === p.sessionId) === -1);
            // Count distinct number of viewers based on participantId
            const distinctParticipants = new Set<string>();
            for (const participant of state.participants) {
                distinctParticipants.add(participant.participantId);
            }
            state.onlineParticipants = distinctParticipants.size;
        },
        meUpdated: (state, action: PayloadAction<ParticipantSession>) => {
            state.me = action.payload;
            storePresenterStatus(state, action);
        },
        presenterUpdated: (state, action: PayloadAction<ParticipantSession | null>) => {
            state.presenter = action.payload;
            storePresenterStatus(state, action);
        },
        vindralChannelUpdated: (state, action: PayloadAction<VindralChannelInformation | null>) => {
            state.vindralChannel = action.payload;
            storePresenterStatus(state, action);
        },
        speakersUpdated: (state, action: PayloadAction<SpeakerStatusInfo[]>) => {
            state.speakers = action.payload;
        },
        speakerStatusChanged: (state, action: PayloadAction<SpeakerStatusInfo>) => {
            const i = state.speakers.findIndex((s) => s.participantId === action.payload.participantId);
            if (action.payload.status === SpeakerStatus.Offline) {
                if (i >= 0) state.speakers.splice(i, 1);
            } else if (i >= 0) {
                state.speakers[i] = action.payload;
            } else {
                state.speakers.push(action.payload);
            }
        },
        notifiedSpeaker: (state, action: PayloadAction<string>) => {
            // NotifySpeaker is broadcast to the whole SpeakersChannel group, so only react if it's about "me"
            if (state.me?.participantId === action.payload) {
                state.notifiedAt = new Date().toISOString();
            }
        },
        // Set by the setSpeakerStatus saga after a successful SetStatus call, so ReportSpeaker can re-send it
        mySpeakerStatusUpdated: (state, action: PayloadAction<SpeakerStatus>) => {
            state.mySpeakerStatus = action.payload;
        },
        rtmpIngestInformationUpdated: (state, action: PayloadAction<IngestInformation | null>) => {
            state.rtmpIngestInformation = action.payload;
        },
        srtIngestInformationUpdated: (state, action: PayloadAction<IngestInformation | null>) => {
            state.srtIngestInformation = action.payload;
        },
        bookingUpdated: (state, action: PayloadAction<Booking | null>) => {
            if (action.payload && action.payload.status !== BookingStatus.Unavailable && action.payload.status !== BookingStatus.Deleted) {
                state.tierId = action.payload.tierId;
                state.earlyStartOn = action.payload.earlyStartOn;
                state.startOn = action.payload.startOn;
                state.warningOn = action.payload.warningOn;
                state.endOn = action.payload.endOn;
            } else {
                state.tierId = null;
                state.earlyStartOn = null;
                state.startOn = null;
                state.warningOn = null;
                state.endOn = null;
            }
            storePresenterStatus(state, action);
        },
        surveyUpdated: (state, action: PayloadAction<Survey>) => {
            state.survey = action.payload;
        },
        surveyAnswersUpdated: (state, action: PayloadAction<ParticipantAnswers>) => {
            state.surveyAnswers = action.payload;
        },

        examUpdated: (state, action: PayloadAction<Exam>) => {
            state.exam = action.payload;
        },

        examAnswersUpdated: (state, action: PayloadAction<ParticipantAnswers>) => {
            state.examAnswers = action.payload;
        },

        joinedChatChannel: (state, action: PayloadAction<JoinedChatChannel>) => {
            if (!action.payload.continued && action.payload.channelId in state.chatChannels) {
                state.chatChannels[action.payload.channelId].messages = [];
            }

            if (!(action.payload.channelId in state.chatChannels)) {
                state.chatChannels[action.payload.channelId] = {
                    channelId: action.payload.channelId,
                    messages: [],
                    noOfUnreadMessages: 0,
                    unreadTimestamp: new Date().toISOString()
                };
            }

            for (const message of action.payload.messages) {
                storeChatMessage(state, {
                    type: 'store',
                    payload: {
                        channelId: action.payload.channelId,
                        message: message
                    }
                });
            }

            // On initial join we do not want unread messages set
            if (ChatChannelIds.isSystemChannel(action.payload.channelId)) {
                state.chatChannels[action.payload.channelId].noOfUnreadMessages = 0;
            } else if (state.chatChannels[action.payload.channelId].messages.length === 0) {
                // Try to find the channel name
                let channelName = state.participants.find((p) => p.participantId === action.payload.channelId)?.nickname;
                channelName ||= state.participants.find((p) => p.participantId === action.payload.channelId)?.participantId;
                state.chatChannels[action.payload.channelId].channelName = channelName;
            }

            // Add the channel to the list of joined channels only if it's not already there
            if (!state.joinedChannels.includes(action.payload.channelId)) {
                state.joinedChannels.push(action.payload.channelId);
            }
            let joinedChannel = state.joinedChannelsInfo[action.payload.channelId];
            if (!joinedChannel) {
                joinedChannel = {
                    channelName: state.chatChannels[action.payload.channelId]?.channelName,
                    noOfUnreadMessages: 0,
                    unreadTimestamp: ''
                };
                state.joinedChannelsInfo[action.payload.channelId] = joinedChannel;
            }

            joinedChannel.noOfUnreadMessages = state.chatChannels[action.payload.channelId].noOfUnreadMessages;
            joinedChannel.unreadTimestamp = state.chatChannels[action.payload.channelId].unreadTimestamp;
        },
        chatMessage: (state, action: PayloadAction<{ channelId: string; chatMessage: ChatMessage }>) => {
            state.unreadChatMessages++;

            storeChatMessage(state, {
                type: 'store',
                payload: {
                    channelId: action.payload.channelId,
                    message: action.payload.chatMessage
                }
            });
        },
        leftChatChannel: (state, action: PayloadAction<string>) => {
            state.joinedChannels = [...state.joinedChannels.filter((c) => c !== action.payload)];
            delete state.joinedChannelsInfo[action.payload];
            if (action.payload in state.chatChannels) {
                state.chatChannels[action.payload].messages = []; // Forget all messages from this channel
            }
        },
        chatChannelUnreadMessages: (state, action: PayloadAction<ChatChannelUnreadMessageCount[]>) => {
            if (state.me?.clientType === ClientType.viewer) {
                // These should never arrive for a client, but lets make sure we don't process it
                return;
            }

            for (let channel of action.payload) {
                if (!(channel.channelId in state.chatChannels)) {
                    state.chatChannels[channel.channelId] = {
                        channelId: channel.channelId,
                        messages: [],
                        noOfUnreadMessages: 0,
                        unreadTimestamp: new Date().toISOString()
                    };
                }

                // Do not modify the unread timestamp if we are already in the channel
                if (!state.joinedChannelsInfo[channel.channelId]) {
                    // Only modify the unread timestamp if we have no unread messages
                    if (state.chatChannels[channel.channelId].noOfUnreadMessages === 0) {
                        state.chatChannels[channel.channelId].unreadTimestamp = new Date().toISOString();
                    }
                }

                state.chatChannels[channel.channelId].noOfUnreadMessages = channel.noOfUnreadMessages;

                // Check if we have joined this channel
                if (state.joinedChannelsInfo[channel.channelId]) {
                    state.joinedChannelsInfo[channel.channelId].noOfUnreadMessages = channel.noOfUnreadMessages;
                    state.joinedChannelsInfo[channel.channelId].unreadTimestamp = state.chatChannels[channel.channelId].unreadTimestamp;
                }
            }
        },
        chatMessageDeleted: (state, action: PayloadAction<{ channelId: string; messageId: string; isPermanentlyDeleted: boolean }>) => {
            if (!(action.payload.channelId in state.chatChannels)) {
                return;
            }

            for (let channelId of [
                ChatChannelIds.allChannel,
                ChatChannelIds.moderatorChannel,
                ChatChannelIds.presenterChannel,
                ChatChannelIds.publicChannel,
                action.payload.channelId
            ]) {
                if (!(channelId in state.chatChannels)) {
                    continue;
                }

                const channel = state.chatChannels[channelId];
                for (let message of channel.messages) {
                    if (message.messageId === action.payload.messageId) {
                        message.isPermanentlyDeleted = action.payload.isPermanentlyDeleted;
                        message.isDeleted = true;
                        message.message = '';
                    }
                    if (message.relatedMessage?.messageId === action.payload.messageId) {
                        message.relatedMessage.isPermanentlyDeleted = action.payload.isPermanentlyDeleted;
                        message.relatedMessage.isDeleted = true;
                        message.relatedMessage.message = '';
                    }
                }
                // Filter out all permanently deleted messages
                channel.messages = channel.messages.filter((m) => !m.isPermanentlyDeleted);
            }
        },
        dataConnectionStatusUpdated: (state, action: PayloadAction<ConnectionStatus>) => {
            state.dataConnection = action.payload;
            storePresenterStatus(state, action);
        },
        serverTimeUpdated: (state, action: PayloadAction<string>) => {
            state.serverTime = action.payload;

            // Abuse server time to update recorderStatus if it's been too long since we last got an update
            if (state.recorderStatusUpdatedOn) {
                const lastUpdate = new Date(state.recorderStatusUpdatedOn);
                if (new Date().getTime() - lastUpdate.getTime() > 5000) {
                    state.recorderStatus = RecorderStatus.Error;
                    state.recorderStatusUpdatedOn = new Date().toISOString();
                }
            }

            storePresenterStatus(state, action);
        },
        nicknameUpdated: (state, action: PayloadAction<{ participantId: string; nickname: string }>) => {
            if (state.me?.participantId === action.payload.participantId) {
                state.me.nickname = action.payload.nickname;
            }

            for (let participant of state.participants) {
                if (participant.participantId === action.payload.participantId) {
                    participant.nickname = action.payload.nickname;
                }
            }
        },

        recoderStatusUpdated: (state, action: PayloadAction<RecorderStatus>) => {
            state.recorderStatus = action.payload;
            state.recorderStatusUpdatedOn = new Date().toISOString();
        },

        // The actions below are handled by saga's but are here to let you know they exist
        setLiveStatus: (state, action: PayloadAction<LiveStatus>) => {},

        startWebinar: (state) => {},
        stopWebinar: (state) => {
            storePresenterStatus(state, null!);
        },

        joinChatChannel: (state, action: PayloadAction<string>) => {},
        leaveChatChannel: (state, action: PayloadAction<string>) => {},
        sendChatMessage: (state, action: PayloadAction<{ channelId: string; message: string }>) => {},
        replyChatMessage: (state, action: PayloadAction<{ channelId: string; message: string; relatedMessageId: string }>) => {},
        forwardChatMessage: (state, action: PayloadAction<{ channelId: string; message: string; relatedMessageId: string }>) => {},
        deleteChatMessage: (state, action: PayloadAction<{ channelId: string; messageId: string }>) => {},
        markChatChannelAsRead: (state, action: PayloadAction<string>) => {
            if (ChatChannelIds.isSystemChannel(action.payload) && state.chatChannels[action.payload]) {
                state.chatChannels[action.payload].noOfUnreadMessages = 0;

                if (state.joinedChannelsInfo[action.payload]) {
                    state.joinedChannelsInfo[action.payload].noOfUnreadMessages = 0;
                }
            }
            state.unreadChatMessages = 0;
        },
        markChatChannelAsUnread: (state, action: PayloadAction<string>) => {},

        startPoll: (state, action: PayloadAction<string>) => {},
        closePoll: (state) => {},
        hidePoll: (state) => {},
        voteOnPoll: (state, action: PayloadAction<VoteOnPollRequest>) => {
            const votes = state.myPollVotes.filter((p) => p.pollId !== action.payload.pollId);
            votes.push({
                pollId: action.payload.pollId,
                optionId: action.payload.optionId,
                clientType: state.me?.clientType ?? ClientType.viewer
            });
            state.myPollVotes = votes;
        },
        myPollVotes: (state, action: PayloadAction<PollVoteSlim[]>) => {
            state.myPollVotes = action.payload;
        },

        setActiveSlide: (state, action: PayloadAction<number>) => {},
        setActiveChapter: (state, action: PayloadAction<string>) => {},

        refreshVindralChannel: (state) => {},
        refreshRtmpIngestInformation: (state) => {},
        refreshSrtIngestInformation: (state) => {},

        updateNickname: (state, action: PayloadAction<string>) => {},

        claimActivePresenter: (state) => {},

        // The actions below are handled by saga's but are here to let you know they exist
        setSpeakerStatus: (state, action: PayloadAction<SpeakerStatus>) => {},
        kickSpeaker: (state, action: PayloadAction<string>) => {},
        notifySpeaker: (state, action: PayloadAction<string>) => {},

        setVideoLayout: (state, action: PayloadAction<VideoLayout>) => {},
        videoLayoutUpdated: (state, action: PayloadAction<VideoLayout>) => {
            state.videoLayout = action.payload;
            if (action.payload === VideoLayout.DesktopSharing) return;
            state._videoLayout = action.payload;
            if (action.payload !== VideoLayout.Default) return;
            if (state.slideDeck.slides.length === 0) {
                state.videoLayout = VideoLayout.VideoOnly;
            } else {
                state.videoLayout = VideoLayout.Classic;
            }
        },

        setBroadcastState: (state, action: PayloadAction<'broadcasting' | 'initializing' | 'not_broadcasting' | 'error'>) => {
            state.previousBroadcastState = state.broadcastState;
            state.broadcastState = action.payload;

            // Only a fall from 'broadcasting' is a loss. The publisher's own start-up ('initializing',
            // MOQ 'connecting') is not, and treating it as one armed a latch on every fresh webinar that
            // nothing ever opened again. Closed here, in the reducer, so the gate is shut in the same
            // tick the state changes -- not a saga turn later.
            if (state.previousBroadcastState === 'broadcasting' && action.payload !== 'broadcasting') {
                state.mediaInterrupted = true;
                state.locallyPaused = true;
                state.mediaInterruptionSeq += 1;
            }

            storePresenterStatus(state, action);
        },

        setVindralConnectionState: (state, action: PayloadAction<'connected' | 'connecting' | 'disconnected' | 'reconnecting' | 'error'>) => {
            // The payload used to be thrown away. It is the only place the publisher's transport state
            // is visible, and a reconnecting publisher is one the audience is not watching.
            state.videoConnection = { status: action.payload === 'connected' ? LiveStatus.Live : LiveStatus.Offline };
            storePresenterStatus(state, action);
        },

        /**
         * The local grace ran out while we were still cut off from the server. We cannot know what it
         * decided, so we assume the least harmful thing and stop publishing the programme.
         */
        localGraceExpired: (state) => {
            state.locallyPaused = true;
            // audioMuted hangs off presenterStatus; without re-deriving it here the canvas went to the
            // background image while the microphone kept publishing.
            storePresenterStatus(state, null!);
        },

        /**
         * The server has accepted our account of what happened and its status now governs again.
         * This is the only thing that opens the gate -- notably not a successful reconnect, and not
         * the publisher's own recovery.
         */
        broadcastReconciled: (state, action: PayloadAction<number>) => {
            // An interruption that happened while the report was in flight is not covered by it.
            if (action.payload !== state.mediaInterruptionSeq) return;

            state.mediaInterrupted = false;
            state.locallyPaused = false;
            storePresenterStatus(state, null!);
        },

        setDeviceIds: (state, action: PayloadAction<{ audioDeviceId: string; videoDeviceId: string }>) => {
            state.audioDeviceId = action.payload.audioDeviceId;
            state.videoDeviceId = action.payload.videoDeviceId;
            storePresenterStatus(state, action);
        },

        setMoqPublisher: (state, action: PayloadAction<boolean>) => {
            state.moqPublisher = action.payload;
            if (action.payload) state.moqFallback = null;
        },
        // One source of truth: moqPublisher drives the component that publishes and every
        // resolveIngestType call, so flipping it is the whole switch.
        moqFallbackTriggered: {
            reducer: (state, action: PayloadAction<{ reason: MoqFallbackReason; code: string | null; at: number }>) => {
                state.moqPublisher = false;
                state.moqFallback = action.payload;
            },
            prepare: (payload: { reason: MoqFallbackReason; code: string | null }) => ({ payload: { ...payload, at: Date.now() } })
        },
        // Handled by the saga: refreshes the token on an auth rejection, then puts moqFallbackTriggered.
        moqFallbackRequested: (state, action: PayloadAction<{ reason: MoqFallbackReason; code: string | null }>) => {},
        sendMoqPublisherStats: (state, action: PayloadAction<MoqPublisherStatsSample>) => {},
        setBroadcastQuality: (state, action: PayloadAction<BroadcastQuality>) => {
            state.broadcastQuality = action.payload;
        },

        setAudioVideoScores: (state, action: PayloadAction<{ audioScore: number; videoScore: number }>) => {
            state.audioScore = action.payload.audioScore;
            state.videoScore = action.payload.videoScore;
            storePresenterStatus(state, action);
        },

        startWebinarCountdown: (state) => {
            if (!state.isActivePresenter) {
                return; // Only the active presenter can start the countdown
            }

            state.presenterStatus = PresenterStatus.Countdown;
            storePresenterStatus(state, null!);
        },

        hideShowTooEarly: (state) => {
            state.showTooEarly = false;
            storePresenterStatus(state, null!);
        },

        showReconnectWarning: (state) => {
            if (!state.isActivePresenter) {
                return; // Only the active presenter should see the reconnect warning
            }

            const serverTime = state.serverTime ? new Date(state.serverTime) : new Date();
            const endOnTime = state.endOn ? new Date(state.endOn) : serverTime;
            state.showReconnectWarning = serverTime < endOnTime;
        },

        hideReconnectWarning: (state) => {
            state.showReconnectWarning = false;
        },

        sendWebrtcIngestStats: (state, action: PayloadAction<IWebrtcIngestStats>) => {},
        playerStatsSampled: (state, action: PayloadAction<PlayerStatsSample>) => {},

        // Tells the server which source the presenter is broadcasting from. The stream verdict is
        // read off Vindral's edge output, which exists for every broadcast, so the server can only
        // apply it to the customer's encoder if it knows the presenter selected one.
        setIngestType: (state, action: PayloadAction<IngestType>) => {
            // Sticky: once an external encoder has been selected, an operator may still have it
            // publishing to the channel. Switching to a browser transport without stopping it first
            // makes both sides fight over the channel and reconnect in a loop, so the broadcaster
            // keeps warning about it until the presenter goes back to the encoder.
            if (isExternalEncoder(action.payload)) {
                state.hadExternalIngest = true;
            }
        },

        setLastServerAction: (state, action: PayloadAction<{ method: string; result: BaseApiResponse | null } | null>) => {
            if (action.payload?.result && action.payload.result.success === false) {
                logError('Error occurred while processing server {Method} action, response {@Response}', {
                    Method: action.payload.method,
                    Response: action.payload.result,
                    WebinarId: state.webinarId,
                    Participant: state.me
                });

                state.failedServerActions = [...(state.failedServerActions ?? []), action.payload];

                // The countdown popup is modal and has no close button: it unmounts when the
                // presenter leaves Countdown, which only happens once the webinar actually starts.
                // A refused go-live (the encoder gate, or any other conflict) would otherwise leave
                // the presenter on a spinner until they reload. storePresenterStatus deliberately
                // preserves Countdown, so this is the one place that can undo it.
                if ((action.payload.method === 'startWebinar' || action.payload.method === 'setLiveStatus') && state.presenterStatus === PresenterStatus.Countdown) {
                    state.presenterStatus = PresenterStatus.Ready;
                }
            }

            // The presenter asked to go on air and the server agreed. That is the most explicit
            // acknowledgement there is, and the Resume button only shows while the publisher is
            // broadcasting -- so whatever interruption the gate was still guarding is over by their
            // own decision. Without this, a lost report followed by a manual resume left the server
            // live and the client on black.
            if (
                action.payload?.result?.success &&
                (action.payload.method === 'startWebinar' || action.payload.method === 'setLiveStatus')
            ) {
                state.mediaInterrupted = false;
                state.locallyPaused = false;
                storePresenterStatus(state, null!);
            }

            state.lastServerAction = action.payload;
        },

        chatMessageRejected: (state, action: PayloadAction<ChatRejection>) => {
            state.lastChatRejection = action.payload;
        },
        chatRejectionHandled: (state) => {
            state.lastChatRejection = null;
        },

        extendWebinarDuration: (state, action: PayloadAction<Duration>) => {},
        setWebinarExtendedResult: (state, action: PayloadAction<WebinarExtendedResult | null>) => {
            state.webinarExtendedResult = action.payload;
        },

        updateSubtitlesTracks: (state, action: PayloadAction<SubtitlesTrack[]>) => {
            state.subtitles = action.payload;

            // Disable active subtitle track if it's no longer available
            if (state.activeSubtitleTrackId) {
                const track = state.subtitles.find((t) => t.trackId === state.activeSubtitleTrackId);
                if (!track) {
                    state.activeSubtitleTrackId = null;
                }
            }

            if (!state.activeSubtitleTrackId) {
                // Try to find the default subtitle track
                const defaultTrack = state.subtitles.find((t) => t.isEnabled && t.isDefault);
                if (defaultTrack) {
                    state.activeSubtitleTrackId = defaultTrack.trackId;
                }
            }

            // Clear active subtitles
            state.activeSubtitles = filteredSubtitles(state.activeSubtitleTrackId || null, state.subtitles, state.timestamp);
        },

        updateSubtitle: (state, action: PayloadAction<{ trackId: string; subtitle: Subtitle }>) => {
            // NOTE: Subtitles are never updated for disabled tracks
            let track = state.subtitles.find((t) => t.trackId === action.payload.trackId);
            if (!track) {
                track = {
                    trackId: action.payload.trackId,
                    isEnabled: true,
                    isDefault: false,
                    subtitles: []
                };
                state.subtitles = [...state.subtitles, track];
            }

            const existingIndex = track.subtitles.findIndex((s) => s.start === action.payload.subtitle.start);
            if (existingIndex !== -1) {
                // Update existing subtitle
                track.subtitles[existingIndex] = action.payload.subtitle;
            } else {
                // Add new subtitle
                track.subtitles = [...track.subtitles, action.payload.subtitle];
            }

            track.subtitles = track.subtitles.slice().sort((a, b) => a.start.localeCompare(b.start));

            // If this is the active subtitle track, update active subtitles
            if (state.activeSubtitleTrackId === action.payload.trackId) {
                state.activeSubtitles = filteredSubtitles(state.activeSubtitleTrackId || null, state.subtitles, state.timestamp);
            }
        },

        setActiveSubtitleTrackId: (state, action: PayloadAction<string | null>) => {
            if (state.activeSubtitleTrackId === action.payload) {
                return;
            }

            state.activeSubtitleTrackId = action.payload;
            state.activeSubtitles = filteredSubtitles(state.activeSubtitleTrackId || null, state.subtitles, state.timestamp);
        },

        timestampUpdated: (state, action: PayloadAction<string>) => {
            state.timestamp = action.payload;
            state.activeSubtitles = filteredSubtitles(state.activeSubtitleTrackId || null, state.subtitles, state.timestamp);
        }
    }
});

export const {
    initializedWebinar,
    joinWebinar,
    leaveWebinar,
    recoderStatusUpdated,

    activeChapterUpdated,
    chaptersUpdated,

    pollsUpdated,
    activePollUpdated,
    pollVotesUpdated,

    activeSlideUpdated,
    slideDeckUpdated,

    liveStatusUpdated,
    streamValidationUpdated,
    joinedWebinar,
    leftWebinar,
    meUpdated,
    presenterUpdated,
    vindralChannelUpdated,
    speakersUpdated,
    speakerStatusChanged,
    notifiedSpeaker,
    mySpeakerStatusUpdated,
    setSpeakerStatus,
    kickSpeaker,
    notifySpeaker,
    rtmpIngestInformationUpdated,
    srtIngestInformationUpdated,

    joinedChatChannel,
    chatMessage,
    leftChatChannel,
    chatChannelUnreadMessages,
    chatMessageDeleted,

    dataConnectionStatusUpdated,
    serverTimeUpdated,
    bookingUpdated,
    webinarDataUpdated,

    surveyUpdated,
    surveyAnswersUpdated,
    examUpdated,
    examAnswersUpdated,

    setLiveStatus,

    startWebinar,
    stopWebinar,

    startPoll,
    closePoll,
    hidePoll,
    voteOnPoll,
    myPollVotes,

    setActiveSlide,
    setActiveChapter,

    sendChatMessage,
    replyChatMessage,
    forwardChatMessage,
    deleteChatMessage,
    joinChatChannel,
    leaveChatChannel,
    markChatChannelAsRead,
    markChatChannelAsUnread,

    refreshVindralChannel,
    refreshRtmpIngestInformation,
    refreshSrtIngestInformation,

    nicknameUpdated,
    updateNickname,

    claimActivePresenter,

    setVideoLayout,
    videoLayoutUpdated,

    startWebinarCountdown,
    setBroadcastState,
    setVindralConnectionState,
    setDeviceIds,
    setMoqPublisher,
    moqFallbackTriggered,
    moqFallbackRequested,
    sendMoqPublisherStats,
    setBroadcastQuality,
    setAudioVideoScores,
    hideShowTooEarly,
    showReconnectWarning,
    hideReconnectWarning,

    sendWebrtcIngestStats,
    playerStatsSampled,
    localGraceExpired,
    broadcastReconciled,

    setIngestType,
    ingestTypeHydrated,

    setLastServerAction,
    chatMessageRejected,
    chatRejectionHandled,

    extendWebinarDuration,
    setWebinarExtendedResult,

    updateSubtitlesTracks,
    updateSubtitle,
    setActiveSubtitleTrackId,
    timestampUpdated
} = liveWebinarSlice.actions;

/**
 * How the presenter's video reaches Vindral, derived from what they picked in the broadcaster.
 * `videoDeviceId` carries the two external encoder paths as sentinels; anything else is a camera,
 * and the separate MOQ toggle decides which browser transport that camera uses.
 *
 * The encoder gate hangs off this one function: it is what gets reported to the server, what the
 * blocked start button checks, and what decides whether the status panel explains the verdict. A
 * disabled button whose explanation is hidden is exactly the defect this replaced, so both must
 * always read the same value.
 */
export const resolveIngestType = (videoDeviceId: string | null | undefined, moqPublisher: boolean | null | undefined): IngestType => {
    if (videoDeviceId === 'rtmp') return IngestType.Rtmp;
    if (videoDeviceId === 'srt') return IngestType.Srt;
    return moqPublisher ? IngestType.Moq : IngestType.WebRtc;
};

/**
 * Whether this ingest path carries a stream the presenter's own encoder produced. Mirrors
 * StreamValidationRules.IsExternalEncoder on the server; only these can be judged.
 */
export const isExternalEncoder = (ingestType: IngestType): boolean => ingestType === IngestType.Rtmp || ingestType === IngestType.Srt;

/**
 * Whether this verdict refuses go-live. Mirrors StreamValidationRules.IsBlocking on the server -
 * only these values are a confirmed encoder fault, never `Unknown` (not yet observed) or `Ok`.
 */
export const isStreamValidationBlocking = (streamValidation: StreamValidation): boolean =>
    streamValidation === StreamValidation.BlockedAudioChannels ||
    streamValidation === StreamValidation.BlockedVideoLevel ||
    streamValidation === StreamValidation.BlockedAudioChannelsAndVideoLevel;

export default liveWebinarSlice.reducer;
